import { SortObject, TableDefinition } from "../types/table";
import { TableAction, TableActionTypes } from "../types/actions.table";
import { TableSelect } from "../types/select";
import getUpdatedTableSelectedItems from "../functions/getUpdatedTableSelectedItems";
import getSortedTableRows from "../functions/getSortedTableRows";

const initialState: {
    sortedData: Record<string, any>[];
    sortOptions: SortObject;
    tableDefinition: TableDefinition;
    tableHeight: number;
    indexer: string;
    selectedItems: TableSelect[];
    selectAllChecked: boolean;
} = {
    sortedData: [],
    sortOptions: {
        sortKey: "",
        reversed: false,
    },
    tableDefinition: {
        rowTemplate: [],
        defaultSortKey: "",
    },
    tableHeight: 0,
    indexer: "",
    selectedItems: [],
    selectAllChecked: false,
};

const tableReducer = (
    state = initialState,
    action: TableActionTypes
): typeof initialState => {
    let sortKey, reversed;

    switch (action.type) {
        case TableAction.SET_DATA:
            sortKey = state.tableDefinition.defaultSortKey;
            return {
                ...state,
                sortedData: getSortedTableRows({
                    sortKey,
                    unsortedTableRows: [...action.data],
                }),
                indexer: action.indexer,
                sortOptions: {
                    sortKey,
                    reversed: false,
                },
            };
        case TableAction.SET_TABLE_DEFINITION:
            return {
                ...state,
                tableDefinition: { ...action.tableDefinition },
            };
        case TableAction.SET_TABLE_HEIGHT:
            return {
                ...state,
                tableHeight: action.tableHeight,
            };
        case TableAction.SET_SORT_KEY:
            sortKey = action.sortKey || state.tableDefinition.defaultSortKey;
            // i.e. priority: check direct action => check if sortKey is same, alternate reverse pattern
            reversed = action.reversed
                ? action.reversed
                : sortKey === state.sortOptions.sortKey
                ? !state.sortOptions.reversed
                : false;
            return {
                ...state,
                sortedData: getSortedTableRows({
                    sortKey,
                    unsortedTableRows: [...state.sortedData],
                    reversed,
                }),
                sortOptions: {
                    sortKey,
                    reversed,
                },
            };
        case TableAction.SET_COLUMN_HEADER_SIZE:
            return {
                ...state,
                tableDefinition: {
                    ...state.tableDefinition,
                    rowTemplate: state.tableDefinition.rowTemplate.map(
                        (row) => {
                            if (row.accessor !== action.accessor) return row;
                            return {
                                ...row,
                                columnWidth: action.width,
                            };
                        }
                    ),
                },
            };
        case TableAction.SET_COLUMN_CONTENT_SIZE:
            return {
                ...state,
                tableDefinition: {
                    ...state.tableDefinition,
                    rowTemplate: state.tableDefinition.rowTemplate.map(
                        (row) => {
                            if (row.accessor !== action.accessor) return row;
                            return {
                                ...row,
                                contentWidth: action.width,
                            };
                        }
                    ),
                },
            };
        case TableAction.SET_COLUMN_SHOWN:
            return {
                ...state,
                tableDefinition: {
                    ...state.tableDefinition,
                    rowTemplate: state.tableDefinition.rowTemplate.map(
                        (row) => {
                            if (row.accessor !== action.accessor) return row;
                            return {
                                ...row,
                                isShown: action.isShown,
                            };
                        }
                    ),
                },
            };
        case TableAction.SET_SELECTED_ITEMS:
            return {
                ...state,
                selectedItems: [...action.selectedItems],
            };
        case TableAction.ADD_SELECTED_ITEM:
            const selectedItems = getUpdatedTableSelectedItems({
                item: action.item,
                idx: action.idx,
                itemSelectBehavior: action.itemSelectBehavior,
                indexer: state.indexer,
                selectedItems: state.selectAllChecked
                    ? []
                    : [...state.selectedItems],
                sortedData: [...state.sortedData],
            });
            return {
                ...state,
                selectAllChecked: false,
                selectedItems,
            };
        case TableAction.TOGGLE_SELECT_ALL_ITEMS:
            return {
                ...state,
                selectedItems: state.selectAllChecked
                    ? []
                    : state.sortedData.map((item, idx) => ({
                          value: item[state.indexer],
                          idx,
                      })),
                selectAllChecked: !state.selectAllChecked,
            };
        default:
            return state;
    }
};

export default tableReducer;
